import { useCallback, useEffect, useRef, useState } from 'react';
import { usePlayerContext } from '../PlayerContext';
import { useStore } from '../hooks/useStore';
import styles from './Poster.module.css';

export function Poster() {
  const { store, config } = usePlayerContext();
  const isPlaying = useStore(store, useCallback(s => s.isPlaying, []));
  const isOnline = useStore(store, useCallback(s => s.isOnline, []));
  const isVod = useStore(store, useCallback(s => s.isVod, []));
  const hasAnnouncement = useStore(store, useCallback(s => s.announce !== null, []));
  const currentTime = useStore(store, useCallback(s => s.currentTime, []));
  const posterUrl = config.poster ?? config.apiData?.channel_poster;
  const [posterError, setPosterError] = useState(false);
  const [posterTimedOut, setPosterTimedOut] = useState(false);
  const posterImageRef = useRef<HTMLImageElement | null>(null);
  const posterTimeoutRef = useRef<number | null>(null);
  const isVodBeforePlayback = isVod && !isPlaying && currentTime === 0;
  const showPoster = !hasAnnouncement && (!isOnline || (isVodBeforePlayback && Boolean(posterUrl)));

  const clearPosterTimeout = useCallback(() => {
    if (posterTimeoutRef.current !== null) {
      window.clearTimeout(posterTimeoutRef.current);
      posterTimeoutRef.current = null;
    }
  }, []);

  const markPosterLoaded = useCallback(() => {
    clearPosterTimeout();
    setPosterError(false);
    setPosterTimedOut(false);
  }, [clearPosterTimeout]);

  useEffect(() => {
    setPosterError(false);
    setPosterTimedOut(false);
    if (!posterUrl) return;

    posterTimeoutRef.current = window.setTimeout(() => {
      const image = posterImageRef.current;
      if (image?.complete && image.naturalWidth > 0) {
        markPosterLoaded();
        return;
      }
      setPosterTimedOut(true);
      posterTimeoutRef.current = null;
    }, 3000);

    return () => {
      if (posterTimeoutRef.current !== null) {
        window.clearTimeout(posterTimeoutRef.current);
        posterTimeoutRef.current = null;
      }
    };
  }, [markPosterLoaded, posterUrl]);

  useEffect(() => {
    const image = posterImageRef.current;
    if (image?.complete && image.naturalWidth > 0) markPosterLoaded();
  }, [markPosterLoaded, posterUrl]);

  useEffect(() => {
    if (store.getState().showPoster !== showPoster) {
      store.setState({ showPoster });
    }
  }, [showPoster, store]);

  const hidden = !showPoster;
  const showFallback = !isOnline && (!posterUrl || posterError || posterTimedOut);

  return (
    <div
      className={`${styles.poster}${hidden ? ` ${styles.hidden}` : ''}`}
    >
      {posterUrl && !posterError && (
        <img
          ref={posterImageRef}
          src={posterUrl}
          alt=""
          className={styles.preload}
          onError={() => {
            clearPosterTimeout();
            setPosterError(true);
          }}
          onLoad={() => {
            markPosterLoaded();
          }}
        />
      )}
      {showFallback && (
        <p className={styles.fallback}>Стрим оффлайн</p>
      )}
    </div>
  );
}
